import type { ReadConfig } from 'api-read-hook';

import { useApiRead } from 'api-read-hook';

import * as MediaUpload from './MediaUpload';

export type V = {
  '@id': string;
  '@type': 'UserStats';
  user: {
    '@id': string;
    '@type': 'User';
    id: string;
    email: string;
    name: string | null;
    gender: string | null;
    dob: string | null;
    ethnicity: string | null;
    country: string | null;
    height: number | null;
    weight: number | null;
    consentMarketing: boolean | null;
    pregnancyIntent: string | null;
    registeredAt: string;
    deletionRequestedAt: string | null;
    deletionPerformedAt: string | null;
    suspendedAt: string | null;
    lastPeriodStartedAt: string | null;
    generalHealthConditions: Array<{
      '@id': string;
      '@type': 'GeneralHealthCondition';
    }> | null;
    recipePreferences: Array<{
      '@id': string;
      '@type': 'Recipe';
      id: string;
      name: string;
    }>;
    observedSymptomForConditions: Array<{
      '@id': string;
      '@type': 'ReproductiveHealthCondition';
    }> | null;
    diagnoses: Array<{
      '@id': string;
      '@type': 'UserDiagnosis';
      type: string;
      diagnosedBy: string;
      diagnosedAge: number;
      managements: Array<string>;
    }>;
    symptomsAfterMeal: Array<{
      '@id': string;
      '@type': 'Symptom';
      id: string;
      name: string;
      slug: string | null;
      description: string | null;
      category: {
        '@id': string;
        '@type': 'SymptomCategory';
        name: string;
      } | null;
    }>;
    frequentSymptoms: Array<{
      '@id': string;
      '@type': 'Symptom';
      id: string;
      name: string;
      slug: string | null;
      description: string | null;
      category: {
        '@id': string;
        '@type': 'SymptomCategory';
        name: string;
      } | null;
    }>;
    symptomsAroundPeriod: Array<{
      '@id': string;
      '@type': 'Symptom';
      id: string;
      name: string;
      slug: string | null;
      description: string | null;
      category: {
        '@id': string;
        '@type': 'SymptomCategory';
        name: string;
      } | null;
    }>;
    contraceptions: Array<{
      '@id': string;
      '@type': 'UserContraception';
      type: string;
      currentlyUsing: boolean;
      usageLength: number;
      stoppedUsingAt: string | null;
      customSymptom: string | null;
      symptoms: Array<{
        '@id': string;
        '@type': 'Symptom';
        id: string;
        name: string;
        slug: string | null;
        description: string | null;
        category: {
          '@id': string;
          '@type': 'SymptomCategory';
          name: string;
        } | null;
      }>;
    }>;
    lastActiveAt: string | null;
    classifications: Array<{
      '@id': string;
      '@type': 'ClientClassification';
      id: string;
      name: string;
    }>;
    contentTags: Array<{
      '@id': string;
      '@type': 'Tag';
      id: string;
      internalName: string;
      userFacingName: string;
      slug: string | null;
      category: {
        '@id': string;
        '@type': 'TagCategory';
        name: string;
      };
    }>;
  };
  subscription: {
    '@id': string;
    '@type': 'SubscriptionInfo';
    isActive: boolean;
    status: string | null;
    source: string | null;
    licence: {
      '@id': string;
      '@type': 'TeamLicence';
      team: {
        '@id': string;
        '@type': 'OrganisationTeam';
        name: string;
        organisation: {
          '@id': string;
          '@type': 'Organisation';
          id: string;
          name: string;
        };
        licenceCount: number;
      };
      expiresAt: string | null;
      revokedAt: string | null;
    } | null;
    subscription: {
      '@id': string;
      '@type': 'Subscription';
      inactiveAt: string | null;
      stripeSubscriptionPlan: string | null;
      paymentOverdueAt: string | null;
      autoRenewCanceledAt: string | null;
    } | null;
  };
  programProgress: {
    '@id': string;
    '@type': 'ProgramProgress';
    status: string;
    startDate: string | null;
    currentWeekNumber: number | null;
    endDate: string | null;
  };
  metricsOverview: {
    '@id': string;
    '@type': 'MetricsOverview';
    topSymptoms: Array<{
      '@id': string;
      '@type': 'MetricSymptomDateItem';
      symptom: {
        '@id': string;
        '@type': 'Symptom';
        id: string;
        name: string;
        slug: string | null;
        description: string | null;
        category: {
          '@id': string;
          '@type': 'SymptomCategory';
          name: string;
        } | null;
      };
      avgSeverity: number | null;
    }>;
    lastMetricCycle: {
      '@id': string;
      '@type': 'MetricCycle';
      flowType: string;
      isPeriodBleeding: boolean;
      date: string;
    } | null;
    carbs: number | null;
    protein: number | null;
    fat: number | null;
    trackerSetting: {
      '@id': string;
      '@type': 'UserMetricSetting';
      id: string;
      mood: boolean;
      cycleManual: boolean | null;
      symptoms: boolean;
      dailyThoughts: boolean;
      steps: boolean;
      exercise: boolean;
      sleep: boolean;
      food: boolean;
      water: boolean;
    } | null;
  };
  clinicians: Array<{
    '@id': string;
    '@type': 'ClinicianStats';
    isMe: boolean;
    clinicianName: string;
    type: string;
    appointmentsHeld: number;
    appointmentsScheduled: number;
    sessionsInSubscription: number;
  }>;
  notes: Array<{
    '@id': string;
    '@type': 'UserNote';
    title: string;
    text: string;
    date: string;
    addedByMe: boolean | null;
    addedByName: string | null;
  }>;
  recentlyViewedLibraryContent: Array<{
    '@id': string;
    '@type': 'LibraryContent';
    title: string;
    thumb: MediaUpload.BaseView | null;
  }>;
  articleResponsesByUser: number;
};

export function useReadItemStats(id: string, options?: ReadConfig) {
  return useApiRead<V>(`/users/${id}/stats`, options);
}
